<template>
  <v-dialog
    :model-value="isOpenDialog"
    :width="width ?? DEFAULT_DIALOG_WIDTH"
    :max-width="isMobile ? '95%' : maxWidth"
    :persistent="persistent"
    @update:model-value="toggleDialog"
    @after-leave="emits('after-leave')"
  >
    <v-card :class="$style.card">
      <v-card-title :class="$style.cardTitle">
        <slot name="title">
          <div :class="$style.dialogHeader">
            <div :class="$style.dialogTitleWrapper">
              <span :class="$style.dialogTitle">{{ title }}</span>
              <button type="button" @click="closeDialog(false)">
                <v-icon icon="fas fa-solid fa-times" />
              </button>
            </div>
            <p v-if="subtitle" :class="$style.dialogSubtitle">
              {{ subtitle }}
            </p>
          </div>
        </slot>
      </v-card-title>
      <v-card-content
        :style="`height: ${height}; max-height: ${maxHeight}; overflow: auto`"
        :class="[$style.content, $slots.action == null && $style.contentWithPadding]"
      >
        <div
          :class="$style.contentInside"
        >
          <slot />
        </div>
      </v-card-content>
      <v-card-actions v-if="$slots.action" :class="$style.actions">
        <slot name="action" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { notificationService, NotificationType } from '@pfsc/core/ui-services/notification.service';
import { useMobileDetection } from '@pfsc/core/ui-services/use-mobile-detection';
import { ref } from 'vue';
import { confirmDialogSerVice } from '@pfsc/core/ui-services/confirm-dialog.service';
import { DEFAULT_DIALOG_WIDTH } from '../../core/utils/constants/common';
import { globalTranslate } from '../locale/app.i18n';

const { isMobile } = useMobileDetection();

const isOpenDialog = ref(false);

const props = defineProps({
  title: String,
  subtitle: {
    type: String,
    required: false,
  },
  width: [String, Number],
  height: [String, Number],
  maxHeight: [String, Number],
  maxWidth: [String, Number],

  hasTitleDivider: {
    type: Boolean,
    default: true,
  },

  canCloseDialog: {
    type: Boolean,
    default: true,
  },

  warningMessage: {
    type: String,
  },

  hasConfirmDialogWhenClose: {
    type: Boolean,
    default: false,
  },

  persistent: {
    type: Boolean,
    default: false,
  },
});

/**
 * Show fill address warning.
 */
function showWarning() {
  notificationService.notify({
    message: props.warningMessage,
    type: NotificationType.warning,
  });
}

const emits = defineEmits(['close', 'after-leave']);

const openDialog = () => {
  isOpenDialog.value = true;
};

/**
 * Close dialog.
 * @param {boolean} [hasConfirmDialogWhenExit] Whether dialog should closed even data changed.
 */
const closeDialog = (hasConfirmDialogWhenExit = true) => {
  if (!props.canCloseDialog) {
    showWarning();
    return;
  }
  if (hasConfirmDialogWhenExit && props.hasConfirmDialogWhenClose) {
    confirmDialogSerVice.showConfirmDialogWith({
      title: globalTranslate('commonConfirmDialog.confirmation'),
      content: globalTranslate('commonConfirmDialog.unsavedChange'),
      onConfirmClick: () => {
        isOpenDialog.value = false;
        emits('close');
      },
    });
    return;
  }
  isOpenDialog.value = false;
  emits('close');
};

const toggleDialog = () => {
  if (!isOpenDialog.value) {
    isOpenDialog.value = true;
    return;
  }
  closeDialog();
};

defineExpose({ openDialog, closeDialog });
</script>

<style module>
:root {
  --padding: 12px;
  @media (min-width: 480px) {
    --padding: 24px;
  }
}

.card {
  border-radius: 12px !important;
  background-color: #F4F4F4;
}

.content {
  height: 100%;
  padding-inline: var(--padding);
}

.contentWithPadding {
  padding-bottom: var(--padding);
}

.contentInside {
  padding: var(--padding);
  background-color: white;
  border-radius: 12px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

.actions {
  padding: var(--padding);
}

.cardTitle {
  padding-block: var(--padding);
  padding-inline: var(--padding);
}

.dialogHeader {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.dialogTitleWrapper{
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dialogTitle {
  font-weight: 600;
  font-size: 22px;
}

.dialogSubtitle {
  margin: 0;
  font-size: var(--font-size-md);
}
</style>
